import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import BlogCard from './components/BlogCard';
import FeaturedArticle from './components/FeaturedArticle';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import NewsletterSection from './components/NewsletterSection';
import ResourcesSection from './components/ResourcesSection';

const TechnicalBlog = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const articles = [
  {
    id: 1,
    slug: "optimizing-react-performance-production",
    title: "Optimizing React Performance for Production: A Comprehensive Guide",
    excerpt: "Learn advanced techniques to improve React application performance, including code splitting, lazy loading, memoization, and virtual DOM optimization strategies that reduce bundle size by 40% and improve load times.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12225c17b-1765211045033.png",
    imageAlt: "Modern laptop displaying colorful code editor with React components and performance metrics on dark background",
    category: "Performance",
    difficulty: "advanced",
    readTime: 12,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 28, 2025",
    tags: ["React", "Performance", "Optimization", "Production"],
    featured: true
  },
  {
    id: 2,
    slug: "building-scalable-nodejs-apis",
    title: "Building Scalable Node.js APIs: Architecture Patterns That Work",
    excerpt: "Explore proven architectural patterns for building maintainable and scalable Node.js APIs, including microservices design, database optimization, caching strategies, and error handling best practices.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfdae944-1767030190306.png",
    imageAlt: "Abstract visualization of interconnected server nodes and API endpoints with glowing blue network connections",
    category: "Backend",
    difficulty: "intermediate",
    readTime: 15,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 25, 2025",
    tags: ["Node.js", "API", "Architecture", "Scalability"]
  },
  {
    id: 3,
    slug: "modern-css-grid-layouts",
    title: "Modern CSS Grid Layouts: From Basics to Advanced Techniques",
    excerpt: "Master CSS Grid with practical examples covering responsive layouts, auto-placement algorithms, grid template areas, and advanced techniques for creating complex, maintainable layouts without frameworks.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d0b73e0-1764764623950.png",
    imageAlt: "Designer workspace showing grid layout sketches on paper with colorful markers and laptop displaying CSS code",
    category: "Frontend",
    difficulty: "beginner",
    readTime: 8,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 22, 2025",
    tags: ["CSS", "Grid", "Layout", "Responsive"]
  },
  {
    id: 4,
    slug: "docker-kubernetes-deployment-guide",
    title: "Docker & Kubernetes: Complete Deployment Guide for Production Apps",
    excerpt: "Step-by-step guide to containerizing applications with Docker and orchestrating them with Kubernetes, covering CI/CD integration, monitoring, scaling strategies, and security best practices.",
    image: "https://images.unsplash.com/photo-1577385079498-710ba72c9c3d",
    imageAlt: "Server room with blue LED lights showing rows of modern data center equipment and network cables",
    category: "DevOps",
    difficulty: "advanced",
    readTime: 18,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 20, 2025",
    tags: ["Docker", "Kubernetes", "DevOps", "Deployment"]
  },
  {
    id: 5,
    slug: "typescript-advanced-patterns",
    title: "TypeScript Advanced Patterns: Type Safety at Scale",
    excerpt: "Deep dive into advanced TypeScript patterns including conditional types, mapped types, template literal types, and utility types that improve code quality and developer experience in large codebases.",
    image: "https://images.unsplash.com/photo-1724811077293-dd328849ccab",
    imageAlt: "Close-up of TypeScript code on monitor showing type definitions and interfaces with syntax highlighting",
    category: "Full-Stack",
    difficulty: "advanced",
    readTime: 14,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 18, 2025",
    tags: ["TypeScript", "Types", "Patterns", "Best Practices"]
  },
  {
    id: 6,
    slug: "database-optimization-strategies",
    title: "Database Optimization Strategies: From Slow to Lightning Fast",
    excerpt: "Practical techniques for optimizing database performance including indexing strategies, query optimization, connection pooling, caching layers, and monitoring tools that reduce query time by 80%.",
    image: "https://images.unsplash.com/photo-1649682892309-e10e0b7cd40b",
    imageAlt: "Abstract representation of database tables and relationships with glowing data connections on dark background",
    category: "Backend",
    difficulty: "intermediate",
    readTime: 11,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 15, 2025",
    tags: ["Database", "Optimization", "Performance", "SQL"]
  },
  {
    id: 7,
    slug: "react-hooks-complete-guide",
    title: "React Hooks: Complete Guide to Custom Hooks and Best Practices",
    excerpt: "Comprehensive guide to React Hooks covering useState, useEffect, useContext, and creating custom hooks for reusable logic, with real-world examples and common pitfalls to avoid.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3c86ae3-1764669618316.png",
    imageAlt: "Developer hands typing on mechanical keyboard with React documentation visible on dual monitors",
    category: "Frontend",
    difficulty: "intermediate",
    readTime: 10,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 12, 2025",
    tags: ["React", "Hooks", "JavaScript", "Frontend"]
  },
  {
    id: 8,
    slug: "microservices-architecture-patterns",
    title: "Microservices Architecture: Patterns, Pitfalls, and Best Practices",
    excerpt: "Explore microservices architecture patterns including service discovery, API gateways, event-driven communication, and distributed tracing, with lessons learned from production implementations.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1534f1e36-1764671525611.png",
    imageAlt: "Futuristic visualization of interconnected microservices with glowing nodes and data flow paths",
    category: "Architecture",
    difficulty: "advanced",
    readTime: 16,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 10, 2025",
    tags: ["Microservices", "Architecture", "Distributed Systems", "Scalability"]
  },
  {
    id: 9,
    slug: "web-security-essentials",
    title: "Web Security Essentials: Protecting Your Applications from Common Threats",
    excerpt: "Essential security practices for web applications covering XSS, CSRF, SQL injection prevention, authentication best practices, and implementing security headers for production environments.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d1b5f69-1764669605749.png",
    imageAlt: "Digital security concept showing padlock icon with binary code and encryption symbols on blue background",
    category: "Full-Stack",
    difficulty: "intermediate",
    readTime: 13,
    author: "MR MIKE",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2c6b13-1763296937529.png",
    authorImageAlt: "Professional headshot of confident male developer with short dark hair wearing casual blue shirt",
    date: "Dec 8, 2025",
    tags: ["Security", "Web Development", "Best Practices", "Authentication"]
  }];


  const categories = ['All', 'Full-Stack', 'Performance', 'Architecture', 'DevOps', 'Frontend', 'Backend'];

  const filteredArticles = useMemo(() => {
    return articles?.filter((article) => {
      const matchesCategory = activeCategory === 'All' || article?.category === activeCategory;
      const matchesSearch = searchQuery === '' ||
      article?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      article?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      article?.tags?.some((tag) => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, articles]);

  const featuredArticle = articles?.find((article) => article?.featured);
  const regularArticles = filteredArticles?.filter((article) => !article?.featured);

  return (
    <>
      <Helmet>
        <title>Technical Blog - MikeDev Pro | Full-Stack Development Insights</title>
        <meta name="description" content="Expert insights on React, Node.js, system architecture, and production-grade development. Learn from real-world implementations and industry best practices." />
        <meta name="keywords" content="React tutorials, Node.js guides, web development, system architecture, performance optimization, technical blog" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <Breadcrumbs />

            <section className="mb-12 md:mb-16">
              <div className="text-center mb-8 md:mb-12">
                <div className="flex justify-center mb-4">
                  <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10">
                    <Icon name="BookOpen" size={32} color="var(--color-primary)" />
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4">
                  Technical Insights & Industry Perspectives
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
                  Production-proven strategies, architectural patterns, and real-world solutions from building scalable applications. Learn from implementations that matter.
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 md:gap-6 mb-8">
                <div className="flex-1">
                  <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                </div>
              </div>

              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory} />

            </section>

            {featuredArticle && searchQuery === '' && activeCategory === 'All' &&
            <section className="mb-12 md:mb-16">
                <FeaturedArticle article={featuredArticle} />
              </section>
            }

            <section className="mb-12 md:mb-16">
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground">
                  {searchQuery ? 'Search Results' : activeCategory === 'All' ? 'Latest Articles' : `${activeCategory} Articles`}
                </h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="FileText" size={16} />
                  <span>{regularArticles?.length} {regularArticles?.length === 1 ? 'article' : 'articles'}</span>
                </div>
              </div>

              {regularArticles?.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {regularArticles?.map((article) =>
                <BlogCard key={article?.id} article={article} />
                )}
                </div> :

              <div className="text-center py-12 md:py-16">
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                      <Icon name="Search" size={32} className="text-muted-foreground" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold font-heading text-foreground mb-2">
                    No articles found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your search or filter criteria
                  </p>
                  <button
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('All');
                  }}
                  className="px-6 py-3 rounded-lg font-medium bg-primary text-primary-foreground hover:shadow-lg transition-all duration-300">

                    Clear Filters
                  </button>
                </div>
              }
            </section>

            <section className="mb-12 md:mb-16">
              <ResourcesSection />
            </section>

            <section>
              <NewsletterSection />
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default TechnicalBlog;